import { ContentBlock, EditorState } from 'draft-js';
import * as chrono from 'chrono-node';
import dateParser from '@support/dateParser';
import addFollowup from './modifiers/addFollowup';
import updateFollowup from './modifiers/updateFollowup';
import { FollowupEntityState } from './utils/detectFollowupEntity';
import isSkipParsingEntityIntersected from './utils/isSkipParsingEntityIntersected';
import { FollowupPluginStore } from './types';
import followupFromParsedResult from './utils/followupFromParsedResult';
import removeFollowupWithoutForgetting from './modifiers/removeFollowupWithoutForgetting';

const parseForDates = (text: string): Array<chrono.ParsedResult> =>
  dateParser.parse(text, new Date());

export const addFollowupIfCandidateFound = (
  editorState: EditorState,
  store: FollowupPluginStore,
): EditorState => {
  const blocksArr = editorState.getCurrentContent().getBlocksAsArray();

  for (let i = 0; i < blocksArr.length; i++) {
    const block = blocksArr[i];
    const parsedNLPDates = parseForDates(block.getText());

    if (parsedNLPDates.length) {
      for (let j = 0; j < parsedNLPDates.length; j++) {
        const parsedResult = parsedNLPDates[j];

        // ensure that the parsed text does not overlap a "skipFollowupParsing" entity location
        if (!isSkipParsingEntityIntersected(editorState, block, parsedResult)) {
          const { text, occasion } = followupFromParsedResult(
            parsedResult,
            store.defaultReminderTime(),
          );

          if (!occasion.isPast) {
            store.setFollowup({ text, occasion });
            return addFollowup(editorState, block, { text });
          }
        }
      }
    }
  }

  return editorState;
};

export const updateFollowupIfParsable = (
  editorState: EditorState,
  store: FollowupPluginStore,
  focusedBlock: ContentBlock,
  followupEntityState: FollowupEntityState,
): EditorState => {
  const startIdx = followupEntityState.offset;
  const endIdx = startIdx + followupEntityState.data?.text?.length;
  const textToParse = focusedBlock.getText().slice(0);
  const parsedNLPDates = parseForDates(textToParse);
  const storedFollowup = store.followupData();

  // If we find nothing parsable when we go to update an existing followup entity,
  // we should assume that the surrounding text no longer qualifies as a date and
  // drop the entity
  if (parsedNLPDates.length == 0) {
    store.setFollowup(null);
    return removeFollowupWithoutForgetting(editorState);
  }

  if (parsedNLPDates.length) {
    for (let idx = 0; idx < parsedNLPDates.length; idx++) {
      const parsedResult = parsedNLPDates[idx];

      if (
        parsedResult.index <= startIdx &&
        parsedResult.index + parsedResult.text.length >= endIdx &&
        parsedResult.text.length != storedFollowup.text.length
      ) {
        const { text, occasion } = followupFromParsedResult(
          parsedResult,
          store.defaultReminderTime(),
        );

        if (!occasion.isPast) {
          store.setFollowup({ text, occasion });
          return updateFollowup(editorState, focusedBlock, { text }, followupEntityState.key);
        }
      }
    }
  }

  return editorState;
};
